/**
 * DRAFT — the service terms below (warranty periods, estimate and service-call
 * policy, service area) are taken from what the site already tells customers in
 * its FAQ content. It has NOT been reviewed by an attorney.
 *
 * Deliberately omitted, because they carry real legal consequence and belong to
 * counsel rather than to a draft: any arbitration clause, class-action waiver,
 * and indemnification terms.
 */
import { LegalPage, LegalSection } from '#app/components/legal.tsx'
import { Text } from '#app/components/text.tsx'
import {
	BASE_URL,
	COMPANY_EMAIL,
	COMPANY_NAME,
	COMPANY_PHONE,
	SERVICE_COUNTIES,
} from '#app/utils/constants.ts'
import { getSeoMeta } from '#app/utils/seo/getSeoMeta.ts'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type Route } from './+types/tos'

// Kept out of the index and the sitemap until counsel has reviewed the text.
// Remove `handle` and the `robots` block below once it is approved.
export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const EFFECTIVE_DATE = 'September 24, 2026'

export const meta: Route.MetaFunction = () => {
	return getSeoMeta({
		title: `Terms of Service | ${COMPANY_NAME}`,
		description: `The terms that apply to garage door installation, repair, and maintenance performed by ${COMPANY_NAME}, and to the use of this website.`,
		robots: { noIndex: true, noFollow: false },
		url: BASE_URL + '/tos',
	})
}

export default function TermsOfServiceRoute() {
	return (
		<LegalPage heading="Terms of Service" effectiveDate={EFFECTIVE_DATE}>
			<Text>
				These terms apply to the garage door services {COMPANY_NAME}{' '}
				(&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) performs for you and
				to your use of this website. The written quote or invoice for a specific job
				controls the details of that job; where it conflicts with these terms, the
				quote or invoice governs.
			</Text>

			<LegalSection title="Services and service area">
				<Text>
					We provide residential and commercial garage door installation, repair, and
					maintenance, including emergency service, across {SERVICE_COUNTIES} in
					Florida. We may decline work that falls outside our service area, that we
					judge unsafe, or that we are not equipped to perform.
				</Text>
			</LegalSection>

			<LegalSection title="Estimates and service calls">
				<Text>
					Estimates for new door and operator installations are free and usually
					involve an on-site visit to take measurements and discuss options. For repair
					work we charge a standard service call and diagnostic fee, which covers the
					technician&rsquo;s time and travel to assess the problem. We offer emergency
					service 24 hours a day; calls outside standard business hours are billed at
					different rates.
				</Text>
				<Text>
					An estimate is based on the condition of the door and the work we can observe
					at the time. If we find additional damage or conditions once work begins, we
					will tell you what changed and what it will cost before continuing.
				</Text>
			</LegalSection>

			<LegalSection title="Scheduling, access, and cancellation">
				<Text>
					You agree to provide safe access to the work area and to keep vehicles,
					stored items, pets, and children clear of it while we work. If we cannot
					access the work area at the scheduled time, we may need to reschedule and may
					charge for the trip. Please give us as much notice as you can if you need to
					cancel or reschedule.
				</Text>
			</LegalSection>

			<LegalSection title="Payment">
				<Text>
					Payment is due on completion of the work unless the quote or invoice states
					otherwise. Amounts we quote do not include sales tax or permit fees unless
					stated. Where a job requires parts to be ordered, we may ask for a deposit
					before we place the order.
				</Text>
			</LegalSection>

			<LegalSection title="Warranty">
				<Text>We warrant the labor we perform as follows:</Text>
				<ul className="list-disc space-y-2 pl-6 text-muted-foreground">
					<li>
						<strong>Repair labor:</strong> 90 days from the date the repair is
						completed.
					</li>
					<li>
						<strong>Installation labor:</strong> one year from the date the
						installation is completed.
					</li>
				</ul>
				<Text>
					New doors, operators, and parts carry warranties from their manufacturers.
					Those terms and durations vary by product and brand, they are set by the
					manufacturer rather than by us, and we will give you the details for what we
					install. Specific warranty terms for your job are set out in your quote and
					invoice.
				</Text>
				<Text>
					These warranties cover defects in our workmanship and do not cover normal
					wear, damage from misuse, accident, vehicle impact, storm or flood damage,
					power surges, pest or corrosion damage, work performed or parts installed by
					anyone else, or failure to maintain the door. Reusing existing components at
					your request, such as old tracks or an older opener, may limit what we can
					warrant and may affect a manufacturer&rsquo;s warranty; we will tell you when
					that is the case.
				</Text>
			</LegalSection>

			<LegalSection title="Safety">
				<Text>
					Garage doors are heavy and store significant energy in their springs and
					cables. Do not attempt to adjust, repair, or release a spring, cable, or
					door under tension yourself. If a door has come off its track, has a broken
					spring or cable, or has fallen, keep people away from it and call us.
				</Text>
			</LegalSection>

			<LegalSection title="Disclaimers and limitation of liability">
				<Text>
					Except for the warranties stated above, we provide our services without other
					warranties, express or implied, to the fullest extent Florida law allows. We
					are not responsible for pre-existing conditions of the structure, for damage
					caused by components we did not install, or for loss caused by circumstances
					outside our reasonable control.
				</Text>
				<Text>
					To the fullest extent the law allows, our total liability arising out of a
					job is limited to the amount you paid us for that job, and we are not liable
					for indirect, incidental, or consequential damages. Nothing in these terms
					limits liability that cannot be limited by law, including liability for
					personal injury caused by our negligence.
				</Text>
			</LegalSection>

			<LegalSection title="Using this website">
				<Text>
					The text, images, and logos on this website belong to us or our licensors and
					may not be copied or reused without permission. Please do not attempt to
					interfere with the site, access it by automated means, or use it to send
					unsolicited messages. Prices, product availability, and service descriptions
					shown here may change, and information on the site is general guidance rather
					than advice about your specific door.
				</Text>
			</LegalSection>

			<LegalSection title="Governing law">
				<Text>
					These terms are governed by the laws of the State of Florida, without regard
					to its conflict of laws rules. Any dispute will be brought in the state or
					federal courts serving Seminole County, Florida.
				</Text>
			</LegalSection>

			<LegalSection title="Changes to these terms">
				<Text>
					We may update these terms from time to time. The version in effect when your
					work is performed is the version that applies to that work. When we make
					changes, we will revise the &ldquo;last updated&rdquo; date at the top of this
					page.
				</Text>
			</LegalSection>

			<LegalSection title="Contact us">
				<Text>
					Questions about these terms? Contact {COMPANY_NAME} at{' '}
					<a className="text-primary hover:underline" href={`mailto:${COMPANY_EMAIL}`}>
						{COMPANY_EMAIL}
					</a>{' '}
					or {COMPANY_PHONE}, or write to us at 508 Central Park Drive, Sanford, FL
					32771.
				</Text>
			</LegalSection>
		</LegalPage>
	)
}
